/**
 * Registering the worker.
 *
 * There is no flag. The worker registers on every production load in a browser that supports
 * it. It is proved locally and then shipped, not dogfooded behind a switch in production.
 *
 * A registered service worker is a one-way door: users cannot clear it, and a bad one bricks
 * the app for everyone who has it. The way back out is the kill switch (`kill.ts`), a
 * separate build of `/sw.js` that unregisters itself — see `docs/runbooks/service-worker-rollout.md`.
 */
import { ensurePersistence } from "../storage/persistence.ts";
import { PRECACHE, SKIP_WAITING } from "./messages.ts";

/** The URLs of the `/assets/` files this page load actually fetched. */
export function loadedAssets(entries: readonly { name: string }[], origin: string): string[] {
  return entries
    .map((entry) => entry.name)
    .filter((name) => name.startsWith(`${origin}/assets/`));
}

/** Called when a new worker is installed and waiting behind the running one. */
export type UpdateListener = (apply: () => void) => void;

let onUpdate: UpdateListener | null = null;

/** Register a callback for "a new version is ready". Replaces any previous one. */
export function whenUpdateReady(listener: UpdateListener): void {
  onUpdate = listener;
}

/**
 * Register the worker.
 *
 * Silent about everything. A browser with no service-worker support, a registration that fails — none of those are the scorer's problem and none of
 * them may cost the screen they are on. The app worked without this yesterday.
 */
export function registerServiceWorker(): void {
  // Absent in the Playwright rig, which runs the dev server — a worker registered against
  // Vite's module graph is a well-known source of confusion, and the suite that proves
  // exactly-once delivery must not be perturbed by this work.
  if (!import.meta.env.PROD) return;
  if (!("serviceWorker" in navigator)) return;

  // After load, so registering never competes with the first paint for bandwidth on the
  // connection this whole feature exists to be gentle with.
  //
  // **The readyState check is not belt-and-braces.** This runs from a React effect, and on a
  // warm load — every asset already in the HTTP cache, which is the common case for a
  // returning scorer — `load` has already fired by the time the effect mounts. Listening for
  // an event that is in the past means never registering at all, silently, on exactly the
  // devices that use the app most. Caught by the cold-load browser test, which is the only
  // place it was observable.
  if (document.readyState === "complete") {
    void register();
    return;
  }
  window.addEventListener("load", () => {
    void register();
  });
}

async function register(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      // Module worker, which is what lets it import the allowlist rather than keep a second
      // copy of the route list. A drifted copy fails silently, in exactly the direction that
      // breaks the front door.
      type: "module",
      scope: "/",
    });

    precache(registration);
    watchForUpdate(registration);
    void requestPersistence();
  } catch {
    // Registration failed. The app is exactly what it was before this shipped.
  }
}

/**
 * Tell the worker which entry chunks this build loaded.
 *
 * Read off the page's own resource timings, so it is right by construction with no generated
 * precache manifest to drift from the bundle. Posted to `registration.active` rather than
 * `navigator.serviceWorker.controller`, which is null on the load that first registers —
 * this worker does not call `clients.claim()`, so it controls nothing until the next
 * navigation.
 */
function precache(registration: ServiceWorkerRegistration): void {
  const target = registration.active;
  if (target === null) return;

  try {
    const urls = loadedAssets(
      performance.getEntriesByType("resource"),
      window.location.origin,
    );
    if (urls.length > 0) target.postMessage({ type: PRECACHE, urls });
  } catch {
    // No resource timings. The assets cache on first use instead.
  }
}

/**
 * Notice a new version, and offer it rather than applying it.
 *
 * The worker never calls `skipWaiting` on its own, so a new build sits in `waiting` until
 * somebody asks for it. Without the affordance below that would be a regression on its own
 * terms: today `index.html` is served `no-cache` and a deploy takes effect on the next load,
 * and taking that away silently would pin scorers to an old bundle with no way to tell.
 */
function watchForUpdate(registration: ServiceWorkerRegistration): void {
  const offer = (worker: ServiceWorker): void => {
    onUpdate?.(() => {
      worker.postMessage({ type: SKIP_WAITING });
      // One reload, on the swap. Guarded because `controllerchange` also fires in cases
      // nobody asked for, and a reload loop is a worse bug than a stale bundle.
      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    });
  };

  // Already waiting when this page loaded — the update arrived during a previous visit.
  if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
    offer(registration.waiting);
  }

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (installing === null) return;
    installing.addEventListener("statechange", () => {
      // `controller` non-null means this is an *update*, not the very first install. The
      // first install has nothing to offer: the page is already running that code.
      if (installing.state === "installed" && navigator.serviceWorker.controller !== null) {
        offer(installing);
      }
    });
  });
}

/**
 * Ask the browser not to evict this origin's storage.
 *
 * `storage/db.ts` already probes for persistence; this is the request that makes it mean
 * something. Installed apps with a worker are granted it far more readily, which is what
 * turns "no ball is ever lost" from a claim about IndexedDB into a claim about IndexedDB the
 * browser has agreed to.
 *
 * Chromium grants or refuses this silently, with no prompt, which is why it is called here
 * rather than gated behind a tap — there is nothing for a scorer to be interrupted by. Any
 * browser that *does* prompt is one where this correctly asks after the app has been used
 * rather than on first paint, because registration itself is gated on a real load.
 */
async function requestPersistence(): Promise<void> {
  // Recorded rather than swallowed: a refusal is something to know about, and Settings shows
  // it. The result is otherwise ignored — IndexedDB is still durable when refused, just
  // evictable, and nothing here may cost the scorer a screen.
  const result = await ensurePersistence();
  if (result !== "granted") console.info(`storage persistence: ${result}`);
}
